import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { toast } from '@raystack/apsara';
import { AxiosError } from 'axios';

import { apiClient } from '@src/clients';
import {
  AssistantStep,
  Chat,
  Citations,
  GenerationStatus,
  Message,
  StepResponse,
  StepWithID,
  UserStep
} from '@src/clients/seeker/api';
import StepItem, { ChatItem } from '@src/components/CoPilot/ChatList/StepItem';
import { useCopilotDispatch, useCopilotSelector } from '@src/contexts';
import { setCopilotChatId } from '@src/redux/_reducers/appReducer';
import {
  addMessage,
  createNewChat,
  getChatMessages,
  giveRating,
  retryMessage
} from '@src/utils/apis/SeekerApis';
import {
  COPILOT_HISTORY_LIMIT,
  HISTORY_ATTRS_DEFAULT_VALUE,
  MULTI_CHAT_STEPS,
  ROLE,
  TOOL_TYPES
} from '@src/utils/constants/COPILOT_CONSTANTS';
import { SendMessageProps } from '@src/utils/types/CopilotTypes';

import { useLoadingElement } from './useLoadingElement';

export const useCopilot = () => {
  const chatId = useCopilotSelector(state => state.chatId);
  const chatData = useCopilotSelector(state => state.chatData);
  const pollMessageId = useCopilotSelector(state => state.pollMessageId);
  const isPolling = useCopilotSelector(state => state.isPolling);
  const msgFetchFailed = useCopilotSelector(state => state.msgFetchFailed);
  const dispatch = useCopilotDispatch();

  const reduxDispatch = useDispatch();
  const historyAttrs = useCopilotSelector(state => state.historyAttrs);

  const [loadingHistory, setLoadingHistory] = useState(false);

  const { aoi_id } = useParams();

  const getScreenContext = () => {
    let screenContext = {};
    if (aoi_id) {
      screenContext = {
        aoi_screen: { aoi_id }
      };
    }
    return screenContext;
  };

  const fetchPrompts = async () => {
    dispatch({ isFetchingPrompts: true });
    try {
      const response = await apiClient.seekerApi.prompts.getPromptsPromptsGet();
      if (response?.data) {
        dispatch({ prompts: response.data });
      }
    } catch (e) {
      console.error('Prompts fetch failed', e);
    } finally {
      dispatch({ isFetchingPrompts: false });
    }
  };

  const fetchMessages = async (id: Chat['id'], isMutating = false) => {
    dispatch({
      msgFetchFailed: false,
      resetMessageId: null,
      initializingCopilot: !isMutating
    });

    try {
      let chat_id = id;

      if (!isMutating) {
        const data = await apiClient.seekerApi.chats.getChatByIdChatsIdGet(id);
        chat_id = data?.data?.id;
      }

      if (chat_id) {
        const res = await getChatMessages(id);

        if (res?.data) {
          dispatch({ chatData: res.data });
        }
      }
    } catch (e) {
      if ((e as AxiosError)?.response?.status === 404) {
        console.error('Chat not found:', e);
      } else {
        console.error('Error fetching chat messages:', e);
        dispatch({
          chatData: {},
          msgFetchFailed: true,
          multiChatStep: MULTI_CHAT_STEPS.WELCOME
        });
      }
    } finally {
      dispatch({ initializingCopilot: false });
    }
  };

  const mutateMessages = async (id: Chat['id']) => {
    await fetchMessages(id, true);
  };

  const pollChatStatus = async (
    id: Chat['id'],
    responseId: Message['id'],
    chats: StepResponse
  ) => {
    if (!id) return; // Avoid polling if chatId is not available

    dispatch({ isPolling: true, resetMessageId: null });
    const intervalId = setInterval(async () => {
      try {
        const response = await getChatMessages(id, {
          after_message_id: responseId
        });

        const newData = response.data;

        if (newData.is_finished) {
          if (chats?.steps?.at(-1)?.id === newData?.steps?.[0]?.id) {
            chats?.steps?.pop();
          }
          dispatch({
            chatData: {
              ...newData,
              steps: [...(chats?.steps || []), ...(newData?.steps || [])]
            },
            isPolling: false,
            pollMessageId: ''
          });
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Polling error:', err);
        dispatch({ isPolling: false, resetMessageId: responseId });
        clearInterval(intervalId);
      }
    }, 1000); // Poll every 1 seconds
  };

  const createChat = async () => {
    dispatch({
      chatId: '',
      chatData: null,
      createChatFailed: false,
      isFetchingPrompts: false,
      isPolling: false,
      isSending: false,
      initializingCopilot: false,
      isCreatingChat: true,
      resetMessageId: null
    });
    reduxDispatch(setCopilotChatId(''));
    try {
      const res = await createNewChat();
      const chatId = res.data.id;
      dispatch({
        chatId: chatId,
        multiChatStep: MULTI_CHAT_STEPS.CHAT,
        historyAttrs: HISTORY_ATTRS_DEFAULT_VALUE
      });
      reduxDispatch(setCopilotChatId(chatId));
      return chatId;
    } catch (e) {
      dispatch({ createChatFailed: true });
      console.error('Error creating chat:', e);
    } finally {
      dispatch({ isCreatingChat: false, msgFetchFailed: false, retryCount: 0 });
    }
  };

  const handleMessageCall = async ({
    message,
    setMessage,
    id = chatId
  }: SendMessageProps) => {
    dispatch({ isSending: true });

    try {
      const response = await addMessage(id, {
        message,
        screen_context: getScreenContext()
      });

      setMessage('');
      const newChatData = await getChatMessages(id);

      dispatch({
        isSending: false,
        chatData: newChatData?.data || null,
        initializingCopilot: false,
        pollMessageId: response.data.id || ''
      });

      if (!newChatData.data.is_finished) {
        pollChatStatus(id, response.data.id, newChatData.data);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error('Something went wrong. Please try again.');
      dispatch({ isSending: false });
    }
  };

  const sendMessage = async (props: SendMessageProps) => {
    let id = '';

    if (!chatId || msgFetchFailed) {
      dispatch({ initializingCopilot: true });
      id = await createChat();
    } else {
      id = chatId;
    }

    if (!id) {
      console.error('chatId is missing');
      return;
    }
    handleMessageCall({ id, ...props });
  };

  const fetchChatList = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await apiClient.seekerApi.chats.listChatsChatsGet({
        limit: COPILOT_HISTORY_LIMIT,
        offset: historyAttrs.offset
      });
      const chats = res?.data?.chats || [];

      if (historyAttrs.offset === 0) {
        dispatch({
          historyAttrs: {
            ...historyAttrs,
            chatList: chats,
            hasMore:
              res.data.pagination.total_count >
              COPILOT_HISTORY_LIMIT + historyAttrs.offset
          }
        });
      } else {
        dispatch({
          historyAttrs: {
            ...historyAttrs,
            chatList: [...historyAttrs.chatList, ...chats],
            hasMore:
              res.data.pagination.total_count >
              COPILOT_HISTORY_LIMIT + historyAttrs.offset
          }
        });
      }
    } catch (error) {
      console.error('Chat list fetch failed', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyAttrs?.offset]);

  const historyLoadingElementRef = useLoadingElement(
    loadingHistory,
    historyAttrs.hasMore,
    () => {
      dispatch({
        historyAttrs: {
          ...historyAttrs,
          offset: historyAttrs.offset + COPILOT_HISTORY_LIMIT
        }
      });
    }
  );

  const setMessages = () => {
    const filteredSteps = (chatData?.steps || []).filter(
      (step: StepWithID) =>
        !(
          step.step.role === ROLE.ASSISTANT &&
          step.step.content === null &&
          step.step.tool_calls !== null
        )
    );

    // Combine and rearrange steps: assistant content first, then InformationRetrieval
    const rearrangedSteps = (() => {
      const result: StepWithID[] = [];
      let i = 0;

      while (i < filteredSteps.length) {
        const currentStep = filteredSteps[i];

        if (
          currentStep.step.role === ROLE.TOOL &&
          currentStep.step.name === TOOL_TYPES.INFORMATION
        ) {
          const combinedCitations: Citations[] = [];
          let j = i;

          while (
            j < filteredSteps.length &&
            filteredSteps[j].step.role === ROLE.TOOL &&
            filteredSteps[j].step?.name === TOOL_TYPES.INFORMATION
          ) {
            combinedCitations.push(
              ...(filteredSteps[j].step.tool_data?.citations || [])
            );
            j++;
          }

          const combinedStep: StepWithID = {
            ...currentStep,
            step: {
              ...currentStep.step,
              tool_data: {
                ...currentStep.step.tool_data,
                citations: combinedCitations
              }
            }
          };

          // Check if the next step is an assistant step and rearrange
          const nextStep = filteredSteps[j];
          if (
            nextStep &&
            nextStep.step.role === ROLE.ASSISTANT &&
            nextStep.step.content !== null
          ) {
            result.push(nextStep); // Add the assistant step first
            result.push(combinedStep); // Add the combined InformationRetrieval step
            i = j + 1; // Skip both steps
          } else {
            // No assistant step after, add combined step as is
            result.push(combinedStep);
            i = j;
          }
        } else if (
          currentStep.step.role === ROLE.TOOL &&
          [TOOL_TYPES.INSIGHTS, TOOL_TYPES.INDEX].includes(
            currentStep.step.name
          ) &&
          i + 1 < filteredSteps.length &&
          filteredSteps[i + 1].step.role === ROLE.ASSISTANT
        ) {
          result.push(filteredSteps[i + 1]);
          result.push(filteredSteps[i]);
          i += 2;
        } else {
          // Add other steps as is
          result.push(currentStep);
          i++;
        }
      }

      return result;
    })();

    let index = 0;
    const len = rearrangedSteps.length;
    const result = [];
    while (index < len) {
      let messageLoading = false,
        ratingMessageId = '',
        rating = '';
      const item = rearrangedSteps[index];
      if (item.step.role === ROLE.USER) {
        if (item.id === pollMessageId && isPolling) {
          messageLoading = true;
        }

        // Get user message text content
        const userTextContent = item.step.content || '';

        result.push(
          <ChatItem
            id={item.id}
            key={item.id}
            isUser
            content={
              <StepItem
                item={item}
                index={index}
                key={index + item.id}
                steps={rearrangedSteps}
              />
            }
            textContent={userTextContent}
          />
        );
        const responseSteps = [];
        const assistantTextContents: string[] = [];
        let j = index + 1;

        while (j < len && rearrangedSteps[j].step.role !== ROLE.USER) {
          if (rearrangedSteps[j].id === pollMessageId && isPolling) {
            messageLoading = true;
          }

          if (
            rearrangedSteps[j].step.role === ROLE.ASSISTANT &&
            !!rearrangedSteps[j].step.content
          ) {
            ratingMessageId = rearrangedSteps[j].id;
            rating = (rearrangedSteps[j].step as AssistantStep).rating || '';

            // Collect assistant text content
            assistantTextContents.push(rearrangedSteps[j].step.content || '');

            responseSteps.push(
              <StepItem
                index={j}
                key={rearrangedSteps[j].id}
                item={rearrangedSteps[j]}
                steps={rearrangedSteps}
              />
            );
            if (
              rearrangedSteps[j].step.generation_status ===
              GenerationStatus.Failed
            ) {
              dispatch({ resetMessageId: rearrangedSteps[j].id });
            }
          }
          if (rearrangedSteps[j].step.role !== ROLE.ASSISTANT) {
            responseSteps.push(
              <StepItem
                index={j}
                key={rearrangedSteps[j].id}
                item={rearrangedSteps[j]}
                steps={rearrangedSteps}
              />
            );
          }

          j++;
        }

        // Join all assistant text contents
        const assistantTextContent = assistantTextContents.join('\n\n');

        result.push(
          <ChatItem
            id={ratingMessageId}
            key={ratingMessageId}
            rating={rating}
            isUser={false}
            content={responseSteps}
            textContent={assistantTextContent}
            messageLoading={messageLoading}
            userMessage={item}
          />
        );
        index = j;
      } else {
        index += 1;
      }
    }
    dispatch({ messages: result });
  };

  const rateAssistantMessage = giveRating;

  const retryAssistantMsgGeneration = async (
    userMessage: StepWithID,
    id: Message['id']
  ) => {
    if (!userMessage || !id) return;

    try {
      const step = userMessage.step as UserStep;
      await retryMessage(chatId, id, {
        message: step.content!,
        screen_context: getScreenContext()
      });
      mutateMessages(chatId);
    } catch (error) {
      console.error('Message retry failed:', error);
      toast.error('Failed to retry message generation.', {
        description: 'Please try again.'
      });
    }
  };

  return {
    loadingHistory,
    fetchChatList,
    historyLoadingElementRef,
    sendMessage,
    pollChatStatus,
    fetchMessages,
    mutateMessages,
    fetchPrompts,
    createChat,
    setMessages,
    rateAssistantMessage,
    retryAssistantMsgGeneration
  };
};
